import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './GeneratorV3.css';
import { BPM_RANGES, ROLE_FILTERS, POSITION_FILTERS } from './bpmConstants';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// BPM chip ranges filter — used as shared filter bar across all tabs
// counts: optional object keyed by range_key (e.g. '90-95') with song counts
function BpmChips({ activeBpmRange, onSelect, onClear, counts }) {
  return (
    <div className="v3-bpm-chips">
      {BPM_RANGES.map(range => {
        const isActive = activeBpmRange && activeBpmRange.min === range.min && activeBpmRange.max === range.max;
        const rangeKey = `${range.min}-${range.max}`;
        const count = counts ? counts[rangeKey] : null;
        return (
          <button
            key={range.label}
            className={`v3-bpm-chip ${isActive ? 'active' : ''}`}
            onClick={() => isActive ? onClear() : onSelect(range)}
          >
            {range.label}
            {count != null && <span className="v3-bpm-chip-count">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Role + position filter bar
function RoleFilterBar({ roleFilter, onSelectRole, positionFilter, onSelectPosition }) {
  return (
    <div className="v3-role-filter-bar">
      {ROLE_FILTERS.map(rf => (
        <button
          key={rf.key}
          className={`v3-role-chip ${roleFilter === rf.key ? 'active' : ''}`}
          onClick={() => onSelectRole(roleFilter === rf.key ? null : rf.key)}
        >
          {rf.label}
        </button>
      ))}
      <span className="v3-filter-sep" />
      {POSITION_FILTERS.map(pf => (
        <button
          key={pf.key}
          className={`v3-role-chip v3-pos-chip ${positionFilter === pf.key ? 'active' : ''}`}
          onClick={() => onSelectPosition(positionFilter === pf.key ? null : pf.key)}
        >
          {pf.label}
        </button>
      ))}
    </div>
  );
}

// BPM Number Filter Component (for All Songs — precise min/max entry)
function BPMNumberFilter({
  bpmRangeMin,
  bpmRangeMax,
  setBpmRangeMin,
  setBpmRangeMax,
  minLengthMinutes,
  minLengthSeconds,
  setMinLengthMinutes,
  setMinLengthSeconds
}) {
  const clearBPMFilter = () => {
    setBpmRangeMin(0);
    setBpmRangeMax(200);
  };

  const hasFilter = bpmRangeMin > 0 || bpmRangeMax < 200;

  return (
    <div className="v3-bpm-number-filter">
      <span className="v3-bpm-filter-label">BPM:</span>
      <input
        type="number"
        value={bpmRangeMin}
        onChange={(e) => setBpmRangeMin(parseInt(e.target.value) || 0)}
        min="0"
        max="300"
        className="v3-bpm-number-input"
      />
      <span className="v3-bpm-filter-label">–</span>
      <input
        type="number"
        value={bpmRangeMax}
        onChange={(e) => setBpmRangeMax(parseInt(e.target.value) || 200)}
        min="0"
        max="300"
        className="v3-bpm-number-input"
      />
      {hasFilter && (
        <button className="v3-bpm-clear-btn-inline" onClick={clearBPMFilter}>
          Clear
        </button>
      )}
      <div className="v3-min-length-filter">
        <span className="v3-bpm-filter-label">Min length:</span>
        <input
          type="number"
          value={minLengthMinutes}
          onChange={(e) => setMinLengthMinutes(e.target.value.replace(/\D/g, ''))}
          min="0"
          max="999"
          placeholder="0"
          aria-label="Minimum length minutes"
          className="v3-duration-number-input"
        />
        <span className="v3-duration-separator">:</span>
        <input
          type="number"
          value={minLengthSeconds}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            setMinLengthSeconds(digits === '' ? '' : String(Math.min(Number(digits), 59)));
          }}
          min="0"
          max="59"
          placeholder="00"
          aria-label="Minimum length seconds"
          className="v3-duration-number-input"
        />
      </div>
    </div>
  );
}

// BPM bar color by type
function bpmBarColor(bpm) {
  if (!bpm) return 'var(--text-muted)';
  if (bpm <= 60) return 'var(--red)';
  if (bpm <= 74) return 'var(--orange)';
  if (bpm <= 89) return 'var(--blue)';
  if (bpm <= 105) return 'var(--green)';
  return 'var(--yellow)';
}

function formatLastPlayed(date) {
  if (!date) return 'Never';
  const d = new Date(date);
  const now = new Date();
  const months = (now.getFullYear() - d.getFullYear()) * 12 + now.getMonth() - d.getMonth();
  if (months < 1) return 'This mo';
  if (months < 12) return `${months}mo`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem > 0 ? `${years}yr ${rem}mo` : `${years}yr`;
}

function formatLastPlayedShort(date) {
  if (!date) return 'Never';
  const d = new Date(date);
  // Show the actual playlist date — month + day, and year when it differs
  // from the current year. "Oct 22" or "Oct 22 '24".
  const opts = { month: 'short', day: 'numeric' };
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const base = d.toLocaleDateString('en-US', opts);
  return sameYear ? base : `${base} '${String(d.getFullYear()).slice(-2)}`;
}

function GeneratorV3() {
  // Playlist state
  const [slots, setSlots] = useState([]);
  const [playlistName, setPlaylistName] = useState('New Playlist');
  const [isDirty, setIsDirty] = useState(false);
  const [loadedSpotifyId, setLoadedSpotifyId] = useState(null); // set when opening an existing playlist

  // Sidebar state
  const [activeTab, setActiveTab] = useState('all');
  const [collections, setCollections] = useState([]);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);
  const [activeCollection, setActiveCollection] = useState(null);
  const [collectionSongs, setCollectionSongs] = useState([]);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectionSort, setCollectionSort] = useState('name');
  const [top40Sort, setTop40Sort] = useState('rank');
  const [trackDurations, setTrackDurations] = useState({});
  const [trackYears, setTrackYears] = useState({});
  const [trackGenres, setTrackGenres] = useState({});
  const [activeBpmRange, setActiveBpmRange] = useState(null); // full range object (includes doubleMin/Max for ÷2)
  const [bpmRangeMin, setBpmRangeMin] = useState(0);
  const [bpmRangeMax, setBpmRangeMax] = useState(200);
  const [minLengthMinutes, setMinLengthMinutes] = useState('');
  const [minLengthSeconds, setMinLengthSeconds] = useState('');
  const [roleFilter, setRoleFilter] = useState(null); // null or role key string, persists across tabs
  const [positionFilter, setPositionFilter] = useState(null); // null or position key (pos2/pos3/pos4/possl)
  const [bpmRangeCounts, setBpmRangeCounts] = useState(null); // {rangeKey: count}
  const [sidebarSongs, setSidebarSongs] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarTotal, setSidebarTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [hideFresh, setHideFresh] = useState(true); // Default true = hide recent (checkbox unchecked)

  // Top 40 / New Soon
  const [top40Tracks, setTop40Tracks] = useState([]);
  const [newSoonTracks, setNewSoonTracks] = useState([]);
  const [top40Loading, setTop40Loading] = useState(false);
  const [newSoonLoading, setNewSoonLoading] = useState(false);
  const [newSoonSort, setNewSoonSort] = useState('date');
  const [allSongsSort, setAllSongsSort] = useState({ field: 'last_played', order: 'desc' });

  // Settings
  const [settings, setSettings] = useState({
    newSoonPlaylistId: '2iZYTqCEumVCVURFAhyzWB',
    top40PlaylistId: '6qd48xm5P7JXCxSdIQe4Ur',
    freshMonths: 6,
    defaultTargetMinutes: 52
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showOpenPlaylist, setShowOpenPlaylist] = useState(false);
  const [playlistsList, setPlaylistsList] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncBanner, setSyncBanner] = useState(null); // 'syncing' | 'success' | 'error' | null

  // Shell generator
  const [targetMinutes, setTargetMinutes] = useState(52);
  const [generating, setGenerating] = useState(false);

  // Spotify creation
  const [creating, setCreating] = useState(false);
  const [createdPlaylist, setCreatedPlaylist] = useState(null);

  // Draft
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState([]);
  // const [showDrafts, setShowDrafts] = useState(false); // Unused - drafts UI removed

  // Drag state
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [sidebarDragSong, setSidebarDragSong] = useState(null);
  const [addSlotDragOver, setAddSlotDragOver] = useState(false);

  // Spotify playback state
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Load settings on mount (auto-sync removed per user request)
  useEffect(() => {
    loadSettings();
    fetchBpmRangeCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track dirty state when slots change
  const slotsInitialized = useRef(false);
  useEffect(() => {
    if (slotsInitialized.current) {
      setIsDirty(true);
    } else if (slots.length > 0) {
      slotsInitialized.current = true;
    }
  }, [slots]);

  // Keyboard shortcuts: Cmd+S (save), Cmd+O (open drafts), Cmd+N (new)
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
        if (e.key === 's') {
          e.preventDefault();
          saveDraftRef.current();
        } else if (e.key === 'o') {
          e.preventDefault();
          openPlaylistRef.current();
        } else if (e.key === 'n') {
          e.preventDefault();
          newPlaylistRef.current();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Warn before closing with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Load sidebar songs when tab/filter changes
  useEffect(() => {
    if (activeTab === 'all') {
      fetchSidebarSongs(bpmRangeMin, bpmRangeMax);
    }
    // eslint-disable-next-line
  }, [activeTab, bpmRangeMin, bpmRangeMax, minLengthMinutes, minLengthSeconds, hideFresh, searchQuery, allSongsSort, positionFilter]);

  // Load Top 40 and New Soon when tab switches
  useEffect(() => {
    if (activeTab === 'top40' && top40Tracks.length === 0) fetchTop40();
    if (activeTab === 'newsoon' && newSoonTracks.length === 0) fetchNewSoon();
    if (activeTab === 'collections' && !collectionsLoaded) fetchCollections();
    // eslint-disable-next-line
  }, [activeTab]);

  // Refetch collection when freshness or sort changes
  useEffect(() => {
    if (activeTab === 'collections' && activeCollection) {
      fetchCollectionSongs(activeCollection, collectionSort);
    }
    // eslint-disable-next-line
  }, [hideFresh, collectionSort]);

  // Poll Spotify playback state every 2 seconds
  useEffect(() => {
    const pollPlaybackState = async () => {
      try {
        const res = await axios.get(`${API_BASE}/playback/state`, { withCredentials: true });
        if (res.data.track) {
          setCurrentlyPlaying(res.data.track.id);
          setIsPlaying(res.data.isPlaying);
        } else {
          setCurrentlyPlaying(null);
          setIsPlaying(false);
        }
      } catch (err) {
        // Silently fail - don't spam console on auth errors
        if (err.response?.status !== 401) {
          console.error('Failed to fetch playback state:', err);
        }
      }
    };

    pollPlaybackState(); // Initial fetch
    const interval = setInterval(pollPlaybackState, 2000);
    return () => clearInterval(interval);
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE}/generator/v3/settings`, { withCredentials: true });
      setSettings(res.data.settings);
      setTargetMinutes(res.data.settings.defaultTargetMinutes || 52);
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  // New Soon "Sync" — removes any track from the New Soon Spotify playlist
  // that's been played within the freshness window. Surfaced in Settings.
  // Two-step: preview (dry-run) → confirm → execute.
  const [syncingNewSoon, setSyncingNewSoon] = useState(false);
  const [syncNewSoonPreview, setSyncNewSoonPreview] = useState(null);
  const [syncNewSoonResult, setSyncNewSoonResult] = useState(null);
  const previewSyncNewSoon = async () => {
    if (!settings.newSoonPlaylistId) return;
    setSyncingNewSoon(true);
    setSyncNewSoonResult(null);
    setSyncNewSoonPreview(null);
    try {
      const res = await axios.post(
        `${API_BASE}/generator/v3/spotify-playlist/${settings.newSoonPlaylistId}/sync-fresh`,
        { freshMonths: settings.freshMonths || 6, dryRun: true },
        { withCredentials: true }
      );
      if ((res.data.would_remove || 0) === 0) {
        setSyncNewSoonResult({
          ok: true,
          removed: 0,
          kept: res.data.kept,
          nothingToDo: true,
        });
      } else {
        setSyncNewSoonPreview({
          would_remove: res.data.would_remove,
          kept: res.data.kept,
          removed_tracks: res.data.removed_tracks || [],
          freshMonths: res.data.freshMonths,
        });
      }
    } catch (err) {
      console.error('Failed to preview New Soon sync:', err);
      setSyncNewSoonResult({
        ok: false,
        error: err.response?.data?.error || err.message || 'Preview failed',
      });
    } finally {
      setSyncingNewSoon(false);
    }
  };
  const confirmSyncNewSoon = async () => {
    if (!settings.newSoonPlaylistId) return;
    setSyncingNewSoon(true);
    try {
      const res = await axios.post(
        `${API_BASE}/generator/v3/spotify-playlist/${settings.newSoonPlaylistId}/sync-fresh`,
        { freshMonths: settings.freshMonths || 6, dryRun: false },
        { withCredentials: true }
      );
      setSyncNewSoonResult({
        ok: true,
        removed: res.data.removed,
        kept: res.data.kept,
      });
      setSyncNewSoonPreview(null);
    } catch (err) {
      console.error('Failed to sync New Soon:', err);
      setSyncNewSoonResult({
        ok: false,
        error: err.response?.data?.error || err.message || 'Sync failed',
      });
    } finally {
      setSyncingNewSoon(false);
    }
  };
  const cancelSyncNewSoon = () => {
    setSyncNewSoonPreview(null);
    setSyncNewSoonResult(null);
  };

  const saveSettings = async (newSettings) => {
    try {
      await axios.put(`${API_BASE}/generator/v3/settings`, { settings: newSettings }, { withCredentials: true });
      setSettings(newSettings);
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };

  const fetchBpmRangeCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/generator/v3/bpm-range-counts`, { withCredentials: true });
      setBpmRangeCounts(res.data.counts);
    } catch (err) {
      console.error('Failed to fetch BPM range counts:', err);
    }
  }, []);

  const fetchSidebarSongs = useCallback(async (minBpm, maxBpm) => {
    setSidebarLoading(true);
    try {
      const params = { limit: 500, hideFresh: hideFresh.toString(), freshMonths: settings.freshMonths };
      if (minBpm != null) params.minBpm = minBpm;
      if (maxBpm != null) params.maxBpm = maxBpm;
      const minDurationMs = ((Number(minLengthMinutes) || 0) * 60 + (Number(minLengthSeconds) || 0)) * 1000;
      if (minDurationMs > 0) params.minDurationMs = minDurationMs;
      // Pass double-time range for ÷2 chips so the server includes both ranges
      if (activeBpmRange?.doubleMin && minBpm === activeBpmRange.min && maxBpm === activeBpmRange.max) {
        params.minBpm2 = activeBpmRange.doubleMin;
        params.maxBpm2 = activeBpmRange.doubleMax;
      }
      if (searchQuery) params.search = searchQuery;
      if (activeTab === 'all') {
        params.sortBy = allSongsSort.field;
        params.order = allSongsSort.order;
      }
      // Pass position filter to server
      if (positionFilter) {
        const pf = POSITION_FILTERS.find(p => p.key === positionFilter);
        if (pf) params.position = pf.position;
      }

      const res = await axios.get(`${API_BASE}/generator/v3/songs`, { params, withCredentials: true });
      setSidebarSongs(res.data.songs);
      setSidebarTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch songs:', err);
    }
    setSidebarLoading(false);
  }, [hideFresh, searchQuery, activeTab, settings.freshMonths, allSongsSort, activeBpmRange, positionFilter, minLengthMinutes, minLengthSeconds]);

  // Spotify auth error state
  const [spotifyAuthNeeded, setSpotifyAuthNeeded] = useState(false);
  const [spotifyError, setSpotifyError] = useState(null);

  const fetchTop40 = async () => {
    setTop40Loading(true);
    setSpotifyError(null);
    try {
      const res = await axios.get(`${API_BASE}/generator/v3/spotify-playlist/${settings.top40PlaylistId}`, { withCredentials: true });
      setTop40Tracks(res.data.tracks.slice(0, 40));
      setSpotifyAuthNeeded(false);
    } catch (err) {
      console.error('Failed to fetch Top 40:', err);
      if (err.response?.status === 401 || err.response?.data?.error === 'Spotify not authorized') {
        setSpotifyAuthNeeded(true);
        setSpotifyError('Spotify authorization needed. Please re-authenticate.');
      } else {
        setSpotifyError(err.response?.data?.error || err.message || 'Failed to load Top 40');
      }
    }
    setTop40Loading(false);
  };

  const fetchNewSoon = async () => {
    setNewSoonLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/generator/v3/spotify-playlist/${settings.newSoonPlaylistId}`, { withCredentials: true });
      setNewSoonTracks(res.data.tracks);
    } catch (err) {
      console.error('Failed to fetch New Soon:', err);
      if (err.response?.status === 401 || err.response?.data?.error === 'Spotify not authorized') {
        setSpotifyAuthNeeded(true);
        setSpotifyError('Spotify authorization needed. Please re-authenticate.');
      }
    }
    setNewSoonLoading(false);
  };

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API_BASE}/generator/v3/collections`, { withCredentials: true });
      setCollections(res.data.collections);
      setCollectionsLoaded(true);
    } catch (err) {
      console.error('Failed to fetch collections:', err);
    }
  };

  const fetchCollectionSongs = async (collectionId, sort) => {
    setCollectionLoading(true);
    setActiveCollection(collectionId);
    const sortParam = sort || collectionSort;
    try {
      const params = { sortBy: sortParam };
      if (hideFresh) {
        params.hideFresh = 'true';
        params.freshMonths = settings.freshMonths || 6;
      }
      const res = await axios.get(`${API_BASE}/generator/v3/collections/${collectionId}`, { params, withCredentials: true });
      setCollectionSongs(res.data.songs);
    } catch (err) {
      console.error('Failed to fetch collection songs:', err);
    }
    setCollectionLoading(false);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
  };

  // Shell description (position markers like "sl-9 c-14")
  // const [shellDescription, setShellDescription] = useState(''); // TODO: Implement shell description display

  // Generate shell
  const generateShell = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/generator/v3/generate-shell`, { targetMinutes }, { withCredentials: true });
      
      // Preserve existing songs in slots
      const newSlots = res.data.slots.map((newSlot, index) => {
        const existingSlot = slots[index];
        if (existingSlot?.song) {
          // Keep the existing song, but update slot metadata
          return { ...newSlot, song: existingSlot.song };
        }
        return newSlot;
      });
      
      setSlots(newSlots);
      // setShellDescription(res.data.shellDescription || ''); // TODO: Display shell description
      setCreatedPlaylist(null);
    } catch (err) {
      console.error('Failed to generate shell:', err);
      alert('Failed to generate shell: ' + (err.response?.data?.error || err.message));
    }
    setGenerating(false);
  };

  // Add song to first empty slot or append
  const addSongToPlaylist = (song) => {
    const emptyIndex = slots.findIndex(s => !s.song);
    if (emptyIndex >= 0) {
      const newSlots = [...slots];
      newSlots[emptyIndex] = { ...newSlots[emptyIndex], song };
      setSlots(newSlots);
    } else {
      setSlots([...slots, {
        position: slots.length + 1,
        type: 'flat',
        label: null,
        song
      }]);
    }
  };

  // Add song from sidebar (could be DB song or Spotify track)
  const handleAddSong = (song) => {
    addSongToPlaylist({
      id: song.id || song.db_song_id,
      name: song.name,
      bpm: song.bpm,
      spotify_id: song.spotify_id,
      artists: song.artists || [],
      artist_ids: song.artist_ids || [],
      duration_ms: song.duration_ms,
      play_count: parseInt(song.play_count) || 0,
      last_played: song.last_played
    });
    // Songs stay in New Soon until playlist is saved/created on Spotify
  };

  // Play or pause track
  const playTrack = async (spotifyId) => {
    // If this track is currently playing, pause it
    if (currentlyPlaying === spotifyId && isPlaying) {
      try {
        await axios.post(`${API_BASE}/playback/pause`, {}, { withCredentials: true });
        setIsPlaying(false);
        return;
      } catch (err) {
        console.error('Failed to pause:', err);
      }
    }

    // Otherwise, play the track
    try {
      await axios.post(`${API_BASE}/generator/v3/play`, { spotify_id: spotifyId }, { withCredentials: true });
      setCurrentlyPlaying(spotifyId);
      setIsPlaying(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        alert(err.response?.data?.error || 'Open Spotify on any device first.');
      } else if (status === 401) {
        // Construct auth URL - remove /api suffix and add /auth/spotify
        const authUrl = API_BASE.replace(/\/api$/, '') + '/auth/spotify';
        window.location.href = authUrl;
      } else if (status === 403) {
        alert(err.response?.data?.error || 'Spotify Premium required.');
      } else {
        // Fallback to URI scheme
        window.location.href = `spotify:track:${spotifyId}`;
      }
    }
  };

  const addToNewSoon = async (spotifyId) => {
    try {
      await axios.post(`${API_BASE}/generator/v3/add-to-playlist`, {
        playlist_id: settings.newSoonPlaylistId,
        spotify_id: spotifyId
      }, { withCredentials: true });
      // Brief visual feedback — could enhance later
    } catch (err) {
      alert('Failed to add to New Soon: ' + (err.response?.data?.error || err.message));
    }
  };

  const removeSong = (index) => {
    const newSlots = [...slots];
    newSlots[index] = { ...newSlots[index], song: null };
    setSlots(newSlots);
  };

  // Add empty slot
  const addEmptySlot = () => {
    setSlots([...slots, {
      position: slots.length + 1,
      type: 'flat',
      label: null,
      song: null
    }]);
  };

  // Drag handlers for reordering
  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (sidebarDragSong) {
      // Dropping from sidebar
      const newSlots = [...slots];
      newSlots[dropIndex] = { ...newSlots[dropIndex], song: sidebarDragSong };
      setSlots(newSlots);
      setSidebarDragSong(null);
    } else if (dragIndex !== null && dragIndex !== dropIndex) {
      // Reordering within playlist
      const newSlots = [...slots];
      const [moved] = newSlots.splice(dragIndex, 1);
      newSlots.splice(dropIndex, 0, moved);
      // Reassign positions and preserve labels
      setSlots(newSlots.map((s, i) => ({ ...s, position: i + 1 })));
    }
    
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
    setSidebarDragSong(null);
    setAddSlotDragOver(false);
  };

  // Handle dropping song on "+ Add Slot" button
  const handleAddSlotDrop = (e) => {
    e.preventDefault();
    setAddSlotDragOver(false);
    
    if (sidebarDragSong) {
      // Add new slot with the song
      const newSlot = {
        position: slots.length + 1,
        label: null,
        song: sidebarDragSong
      };
      setSlots([...slots, newSlot]);
      setSidebarDragSong(null);
      setIsDirty(true);
    }
  };

  const handleAddSlotDragOver = (e) => {
    e.preventDefault();
    setAddSlotDragOver(true);
  };

  const handleAddSlotDragLeave = () => {
    setAddSlotDragOver(false);
  };

  // Sidebar song drag
  const handleSidebarDragStart = (song) => {
    setSidebarDragSong({
      id: song.id || song.db_song_id,
      name: song.name,
      bpm: song.bpm,
      spotify_id: song.spotify_id,
      artists: song.artists || [],
      artist_ids: song.artist_ids || [],
      duration_ms: song.duration_ms,
      play_count: parseInt(song.play_count) || 0,
      last_played: song.last_played
    });
  };

  // Set label on a slot
  const toggleLabel = (index, label) => {
    const newSlots = [...slots];
    newSlots[index] = {
      ...newSlots[index],
      label: newSlots[index].label === label ? null : label
    };
    setSlots(newSlots);
  };

  // Save draft
  const saveDraft = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/generator/v3/drafts`, {
        name: playlistName,
        slots
      }, { withCredentials: true });
      setIsDirty(false);
      alert('Draft saved!');
    } catch (err) {
      console.error('Failed to save draft:', err);
      const errorMsg = err.response?.data?.details || err.response?.data?.error || err.message || 'Unknown error';
      const hint = err.response?.data?.hint || '';
      alert(`Failed to save draft: ${errorMsg}${hint ? '\n' + hint : ''}`);

      // If auth error, redirect to login
      if (err.response?.status === 401) {
        if (window.confirm('You need to log in. Redirect to login page?')) {
          window.location.href = '/login';
        }
      }
    }
    setSaving(false);
  };

  const newPlaylist = () => {
    if (isDirty) {
      const answer = window.confirm('You have unsaved changes. Save before creating a new playlist?');
      if (answer) {
        saveDraft().then(() => {
          setSlots([]);
          setPlaylistName('New Playlist');
          setLoadedSpotifyId(null);
          setIsDirty(false);
          slotsInitialized.current = false;
        });
        return;
      }
    }
    setSlots([]);
    setPlaylistName('New Playlist');
    setLoadedSpotifyId(null);
    setIsDirty(false);
    slotsInitialized.current = false;
  };

  // Refs for keyboard shortcuts (avoid stale closures)
  const saveDraftRef = useRef(null);
  const openPlaylistRef = useRef(null);
  const newPlaylistRef = useRef(null);
  useEffect(() => {
    saveDraftRef.current = saveDraft;
    newPlaylistRef.current = newPlaylist;
    openPlaylistRef.current = openPlaylistList;
  });

  // Load drafts list
  // const loadDraftsList = async () => {
  //   try {
  //     const res = await axios.get(`${API_BASE}/generator/v3/drafts`, { withCredentials: true });
  //     setDrafts(res.data.drafts);
  //     setShowDrafts(true);
  //   } catch (err) {
  //     console.error('Failed to load drafts:', err);
  //   }
  // };


  // Load a specific draft
  const loadDraft = async (draftId) => {
    try {
      const res = await axios.get(`${API_BASE}/generator/v3/drafts/${draftId}`, { withCredentials: true });
      setSlots(res.data.draft.slots);
      setPlaylistName(res.data.draft.name);
      // setShowDrafts(false); // Unused
    } catch (err) {
      console.error('Failed to load draft:', err);
    }
  };

  const deleteDraft = async (draftId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API_BASE}/generator/v3/drafts/${draftId}`, { withCredentials: true });
      setDrafts(drafts.filter(d => d.id !== draftId));
    } catch (err) {
      console.error('Failed to delete draft:', err);
    }
  };

  // Open existing playlist
  const openPlaylistList = async () => {
    try {
      const [playlistsRes, draftsRes] = await Promise.all([
        axios.get(`${API_BASE}/generator/v3/playlists-list`, { withCredentials: true }),
        axios.get(`${API_BASE}/generator/v3/drafts`, { withCredentials: true }),
      ]);
      setPlaylistsList(playlistsRes.data.playlists);
      setDrafts(draftsRes.data.drafts);
      setShowOpenPlaylist(true);
    } catch (err) {
      console.error('Failed to load playlists:', err);
    }
  };

  const loadPlaylist = async (playlistId) => {
    try {
      const res = await axios.get(`${API_BASE}/generator/v3/load-playlist/${playlistId}`, { withCredentials: true });
      setSlots(res.data.slots);
      setPlaylistName(res.data.name);
      setLoadedSpotifyId(res.data.spotify_id || null);
      setShowOpenPlaylist(false);
      setIsDirty(false);
      slotsInitialized.current = false;
    } catch (err) {
      alert('Failed to load playlist');
    }
  };

  const deletePlaylist = async (playlistId, playlistName) => {
    if (!window.confirm(`Are you sure you want to delete "${playlistName}"? This will remove it from Spotify and the database.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/generator/v3/playlist/${playlistId}`, { withCredentials: true });
      // Refresh the list
      const [playlistsRes, draftsRes] = await Promise.all([
        axios.get(`${API_BASE}/generator/v3/playlists-list`, { withCredentials: true }),
        axios.get(`${API_BASE}/generator/v3/drafts`, { withCredentials: true }),
      ]);
      setPlaylistsList(playlistsRes.data.playlists);
      setDrafts(draftsRes.data.drafts);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      console.error('Delete playlist error:', err.response?.data);
      alert(`Failed to delete playlist: ${errorMsg}\n\nPlaylist ID: ${playlistId}`);
    }
  };

  // Visibility toggle removed - Spotify API doesn't reliably support changing privacy
  // Icons are display-only and show the current state from Spotify sync

  const syncAllPlaylists = async () => {
    if (syncing) return;
    
    setSyncing(true);
    setSyncBanner('syncing');
    
    try {
      const res = await fetch(`${API_BASE}/admin/trigger-sync`, { 
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      
      if (res.ok && data.message) {
        // Poll for completion with banner
        await pollSyncCompletionWithBanner();
      } else if (data.needsAuth) {
        setSyncBanner('error');
        setTimeout(() => setSyncBanner(null), 5000);
        
        // Detect if we're on localhost or mattsrides.com
        const host = window.location.hostname;
        const authUrl = host === 'localhost' || host === '127.0.0.1'
          ? 'http://localhost:3001/auth/spotify'
          : 'https://mattsrides.com/auth/spotify';
        
        alert(`Spotify token expired. Please re-authenticate at:\n${authUrl}`);
      } else {
        setSyncBanner('error');
        setTimeout(() => setSyncBanner(null), 5000);
      }
    } catch (err) {
      setSyncBanner('error');
      setTimeout(() => setSyncBanner(null), 5000);
    } finally {
      setSyncing(false);
    }
  };

  const pollSyncCompletionWithBanner = async () => {
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;
    
    const check = async () => {
      if (attempts >= maxAttempts) {
        setSyncBanner('error');
        setTimeout(() => setSyncBanner(null), 5000);
        return;
      }
      attempts++;
      
      try {
        const res = await fetch(`${API_BASE}/health`, { credentials: 'include' });
        const data = await res.json();
        const recentLogs = data.recentSyncLogs || [];
        
        const hasCompleted = recentLogs.some(log => 
          log.includes('Sync completed successfully') || 
          log.includes('SUCCESS')
        );
        
        if (hasCompleted) {
          setSyncBanner('success');
          setTimeout(() => setSyncBanner(null), 3000);
          return;
        }
        
        // Check again in 5 seconds
        setTimeout(check, 5000);
      } catch (err) {
        // Silently fail polling, sync may still be running
        setTimeout(check, 5000);
      }
    };
    
    setTimeout(check, 5000); // Start polling after 5 seconds
  };

  // Create or update in Spotify
  const saveToSpotify = async () => {
    setCreating(true);
    let playlistId = loadedSpotifyId;
    
    try {
      if (loadedSpotifyId) {
        await axios.put(`${API_BASE}/generator/v3/update-spotify`, {
          spotify_playlist_id: loadedSpotifyId,
          slots: slots.filter(s => s.song),
          name: playlistName,
          description: computedShellDescription
        }, { withCredentials: true });
        alert('Playlist updated on Spotify!');
        setIsDirty(false);
      } else {
        const res = await axios.post(`${API_BASE}/generator/v3/create-spotify`, {
          slots: slots.filter(s => s.song),
          name: playlistName,
          description: computedShellDescription
        }, { withCredentials: true });
        setCreatedPlaylist(res.data.playlist);
        playlistId = res.data.playlist.id;
        setLoadedSpotifyId(playlistId);
        setIsDirty(false);
      }

      // Sync the playlist back to the database
      if (playlistId) {
        try {
          await axios.post(`${API_BASE}/generator/v3/sync-playlist`, {
            spotify_playlist_id: playlistId
          }, { withCredentials: true });
          console.log('Playlist synced to database');
        } catch (err) {
          console.error('Failed to sync playlist to database:', err);
          // Don't fail the whole operation if sync fails
        }
      }

      // Now remove used songs from New Soon playlist on Spotify
      const usedSpotifyIds = slots
        .filter(s => s.song?.spotify_id)
        .map(s => s.song.spotify_id);
      
      if (usedSpotifyIds.length > 0 && newSoonTracks.length > 0) {
        const toRemove = usedSpotifyIds.filter(id => 
          newSoonTracks.some(t => t.spotify_id === id)
        );
        if (toRemove.length > 0) {
          try {
            await axios.post(`${API_BASE}/generator/v3/remove-from-playlist`, {
              playlist_id: settings.newSoonPlaylistId,
              spotify_ids: toRemove
            }, { withCredentials: true });
            // Update local state
            setNewSoonTracks(prev => prev.filter(t => !toRemove.includes(t.spotify_id)));
          } catch (err) {
            console.error('Failed to remove songs from New Soon:', err);
          }
        }
      }
    } catch (err) {
      alert('Failed to save to Spotify: ' + (err.response?.data?.error || err.message));
    }
    setCreating(false);
  };

  // =====================================================
  // Compute position markers dynamically from current slots
  // =====================================================
  const computedShellDescription = (() => {
    const markers = [];
    const slowIdx = slots.findIndex(s => s.label === 'slow');
    const closerIdx = slots.findIndex(s => s.label === 'closer');
    if (slowIdx >= 0) markers.push(`sl-${slowIdx + 1}`);
    if (closerIdx >= 0) markers.push(`c-${closerIdx + 1}`);
    return markers.join(' ');
  })();

  // =====================================================
  // CHECKLIST VALIDATION
  // =====================================================
  const filledSlots = slots.filter(s => s.song);
  const filledSongs = filledSlots.map(s => s.song);

  // Hard rules
  const artistNames = filledSongs.flatMap(s => s.artists || []);
  const noDuplicateArtists = new Set(artistNames).size === artistNames.length;
  
  
  const noHighBpmFlat = !filledSlots.some(s => 
    (s.type === 'flat' || s.type === 'opener') && s.song?.bpm > 105
  );
  const noLowBpmClimb = !filledSlots.some(s =>
    (s.type === 'climb' || s.type === 'heavy_climb') && s.song?.bpm < 55
  );

  // Structure
  const hasOpener = slots.some(s => s.label === 'opener' && s.song);
  const hasCloser = slots.some(s => s.label === 'closer' && s.song);
  const hasSlowSong = slots.filter(s => s.label === 'slow' && s.song).length === 1;
  const hasSprint = slots.some(s => s.label === 'sprint' && s.song);
  
  const climbSlots = filledSlots.filter(s => s.type === 'climb' || s.type === 'heavy_climb');
  const climbBpms = new Set(climbSlots.map(s => s.song?.bpm));
  const hasThreeClimbs = climbSlots.length >= 3 && climbBpms.size >= 3;
  
  const slowIndex = slots.findIndex(s => s.label === 'slow' && s.song);
  const sprintIndex = slots.findIndex(s => s.label === 'sprint' && s.song);
  const sprintFollowsSlow = slowIndex >= 0 && sprintIndex === slowIndex + 1;

  // Decade checks from Spotify release years
  const songYears = filledSongs.map(s => trackYears[s.spotify_id]).filter(Boolean);
  const hasDecade = (start, end) => songYears.some(y => y >= start && y <= end);
  const has80s = hasDecade(1980, 1989);
  const has90s = hasDecade(1990, 1999);
  const has2020s = hasDecade(2020, 2029);
  const decadesLoaded = filledSongs.length > 0 && songYears.length > 0;

  // Genre checks from Spotify artist genres
  const allSongGenres = filledSongs.flatMap(s => trackGenres[s.spotify_id] || []);
  const genresLoaded = filledSongs.length > 0 && filledSongs.some(s => trackGenres[s.spotify_id]);
  const hasGenre = (patterns) => allSongGenres.some(g => patterns.some(p => g.toLowerCase().includes(p)));
  const hasEDM = hasGenre(['edm', 'electro', 'house', 'dance', 'trance', 'dubstep', 'techno']);
  const hasRap = hasGenre(['rap', 'hip hop', 'hip-hop', 'trap']);
  const hasPunk = hasGenre(['punk']);
  const hasRock = hasGenre(['rock', 'metal']);

  // Back-to-back genre check (now possible with genre data)
  const noBackToBackSameGenre = (() => {
    if (!genresLoaded) return true;
    for (let i = 1; i < filledSlots.length; i++) {
      const prevGenres = trackGenres[filledSlots[i-1].song?.spotify_id] || [];
      const currGenres = trackGenres[filledSlots[i].song?.spotify_id] || [];
      // Check if they share a primary genre category
      const prevCats = new Set(prevGenres.map(g => g.split(' ')[0]));
      const currCats = currGenres.map(g => g.split(' ')[0]);
      if (currCats.some(c => prevCats.has(c)) && prevCats.size > 0) {
        // Exception: EDM back-to-back is allowed
        const isEDM = (genres) => genres.some(g => ['edm', 'electro', 'house', 'dance', 'trance'].some(p => g.includes(p)));
        if (isEDM(prevGenres) && isEDM(currGenres)) continue;
        return false;
      }
    }
    return true;
  })();

  const checklistItems = [
    // Hard rules
    { group: 'hard', label: 'No duplicate artists', passed: noDuplicateArtists },
    { group: 'hard', label: 'No back-to-back same genre', passed: genresLoaded ? noBackToBackSameGenre : true, unknown: !genresLoaded },
    { group: 'hard', label: 'No BPM over 105 for flat', passed: noHighBpmFlat },
    { group: 'hard', label: 'No climb below 55 BPM', passed: noLowBpmClimb },
    // Structure
    { group: 'structure', label: 'Has opener', passed: hasOpener },
    { group: 'structure', label: 'Has closer (banger)', passed: hasCloser },
    { group: 'structure', label: 'Has slow song (1 only)', passed: hasSlowSong },
    { group: 'structure', label: 'Has sprint/power push', passed: hasSprint },
    { group: 'structure', label: '3+ climbs at different BPMs', passed: hasThreeClimbs },
    { group: 'structure', label: 'Sprint follows slow song', passed: sprintFollowsSlow },
    // Variety (real data from Spotify)
    { group: 'variety', label: 'Has an 80s song', passed: has80s, unknown: !decadesLoaded },
    { group: 'variety', label: 'Has a 90s song', passed: has90s, unknown: !decadesLoaded },
    { group: 'variety', label: 'Has a 2020s song', passed: has2020s, unknown: !decadesLoaded },
    { group: 'variety', label: 'Has EDM tracks', passed: hasEDM, unknown: !genresLoaded },
    { group: 'variety', label: 'Has a rap/hip-hop song', passed: hasRap, unknown: !genresLoaded },
    { group: 'variety', label: 'Has punk/rock', passed: hasPunk || hasRock, unknown: !genresLoaded },
  ];

  const verifiableItems = checklistItems.filter(c => !c.unknown);
  const passedCount = verifiableItems.filter(c => c.passed).length;

  // Track metadata — fetch durations, release years, genres from Spotify
  useEffect(() => {
    const missing = filledSongs.filter(s => s.spotify_id && !trackDurations[s.spotify_id]);
    if (missing.length > 0) {
      const ids = missing.map(s => s.spotify_id).join(',');
      axios.get(`${API_BASE}/generator/v3/track-durations?ids=${ids}`, { withCredentials: true })
        .then(res => {
          setTrackDurations(prev => ({ ...prev, ...res.data.durations }));
          if (res.data.releaseYears) setTrackYears(prev => ({ ...prev, ...res.data.releaseYears }));
          if (res.data.trackGenres) setTrackGenres(prev => ({ ...prev, ...res.data.trackGenres }));
        })
        .catch(() => {});
    }
  }, [filledSongs.map(s => s.spotify_id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalDurationMs = filledSongs.reduce((sum, s) => {
    const dur = trackDurations[s.spotify_id] || s.duration_ms || 0;
    return sum + dur;
  }, 0);
  const totalSeconds = Math.floor(totalDurationMs / 1000);
  const durationMin = Math.floor(totalSeconds / 60);
  const durationSec = totalSeconds % 60;

  // BPM flow data
  const bpmData = slots.map((s, i) => ({
    position: i + 1,
    bpm: s.song?.bpm || null,
    label: s.label
  }));

  // BPM chip handlers
  const handleBpmChipSelect = (range) => {
    setActiveBpmRange(range);
    setBpmRangeMin(range.min);
    setBpmRangeMax(range.max);
  };
  const handleBpmChipClear = () => {
    setActiveBpmRange(null);
    setBpmRangeMin(0);
    setBpmRangeMax(200);
  };

  // Client-side BPM match — handles ÷2 double-time ranges
  const matchesBpmFilter = (bpm) => {
    const noFilter = bpmRangeMin === 0 && bpmRangeMax >= 200;
    if (noFilter) return true;
    if (!bpm) return true;
    const inNormal = bpm >= bpmRangeMin && bpm <= bpmRangeMax;
    const inDouble = activeBpmRange?.doubleMin && bpm >= activeBpmRange.doubleMin && bpm <= activeBpmRange.doubleMax;
    return inNormal || !!inDouble;
  };

  // Client-side role match
  const matchesRoleFilter = (song) => {
    if (!roleFilter) return true;
    const roles = song.roles || [];
    return roles.includes(roleFilter);
  };

  // Sorted New Soon tracks
  const sortedNewSoon = [...newSoonTracks].filter(t => {
    if (!matchesBpmFilter(t.bpm)) return false;
    // role filter not applied to Spotify playlist tracks (no role data)
    // Freshness filter
    if (!hideFresh) return true;
    if (!t.last_played) return true;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - (settings.freshMonths || 6));
    return new Date(t.last_played) < cutoff;
  }).sort((a, b) => {
    if (newSoonSort === 'bpm') return (a.bpm || 0) - (b.bpm || 0);
    return new Date(b.added_at) - new Date(a.added_at);
  });

  return (
    <div className="v3-app">
      {/* HEADER */}
      {syncBanner && (
        <div className={`v3-sync-banner v3-sync-banner-${syncBanner}`}>
          {syncBanner === 'syncing' && '🔄 Syncing playlists...'}
          {syncBanner === 'success' && '✅ Sync complete!'}
          {syncBanner === 'error' && '❌ Sync failed - check auth'}
        </div>
      )}
      <header className="v3-header">
        <h1><a href="/">🚴 The Playlist Builder</a></h1>
        <input
          className="v3-playlist-name-input"
          value={playlistName}
          onChange={(e) => setPlaylistName(e.target.value)}
          placeholder="Playlist name..."
        />
        <div className="v3-header-actions">
          <button 
            className="v3-btn v3-btn-sm" 
            onClick={syncAllPlaylists}
            disabled={syncing}
            title="Sync last 100 playlists from Spotify"
          >
            {syncing ? '⏳ Syncing...' : '🔄 Sync'}
          </button>
          <button className="v3-btn v3-btn-sm" onClick={openPlaylistList}>📂 Open</button>
          <button className="v3-btn v3-btn-sm" onClick={() => setShowSettings(true)}>⚙️</button>
          <button className="v3-btn v3-btn-sm" onClick={saveDraft} disabled={saving || loadedSpotifyId}>
            {saving ? '...' : '💾 Save Draft'}
          </button>
          <button 
            className="v3-btn v3-btn-sm v3-btn-success" 
            onClick={saveToSpotify} 
            disabled={creating || filledSlots.length === 0}
          >
            {creating ? '⏳...' : loadedSpotifyId ? '💾 Save to Spotify' : '✨ Create in Spotify'}
          </button>
        </div>
      </header>

      {/* Created success */}
      {createdPlaylist && (
        <div className="v3-success-bar">
          ✅ Created <strong>{createdPlaylist.name}</strong> ({createdPlaylist.trackCount} tracks) — 
          <a href={createdPlaylist.url} target="_blank" rel="noopener noreferrer">Open in Spotify →</a>
        </div>
      )}

      <div className="v3-layout">
        {/* ===== LEFT: PLAYLIST BUILDER ===== */}
        <div className="v3-main">
          {/* Shell Generator */}
          <div className="v3-shell-generator">
            <h2>🎲 Generate Shell</h2>
            <div className="v3-shell-options">
              <div className="v3-shell-field">
                <label>Target Duration</label>
                <select value={targetMinutes} onChange={e => setTargetMinutes(parseInt(e.target.value))}>
                  <option value={49}>49 min (13 songs)</option>
                  <option value={50}>50 min (13 songs)</option>
                  <option value={51}>51 min (14 songs)</option>
                  <option value={52}>52 min (14 songs)</option>
                </select>
              </div>
              <button 
                className="v3-btn v3-btn-primary" 
                onClick={generateShell} 
                disabled={generating}
              >
                {generating ? '⏳ Generating...' : 'Generate →'}
              </button>
            </div>
          </div>

          {/* Playlist Header */}
          <div className="v3-playlist-header">
            <h3>Playlist · {filledSlots.length} songs</h3>
            <div className="v3-playlist-meta">
              <span className={durationMin >= 45 && durationMin <= 55 ? 'good' : ''}>
                {totalDurationMs > 0 ? `${durationMin}:${String(durationSec).padStart(2, '0')}` : '—'}
              </span>
              <span>{noDuplicateArtists ? '0' : '⚠️'} artist dupes</span>
              {computedShellDescription && (
                <span className="v3-shell-markers" title="Slow song and closer positions">{computedShellDescription}</span>
              )}
            </div>
          </div>

          {/* Playlist Slots */}
          <div className="v3-playlist-slots">
            {slots.map((slot, index) => (
              <div
                key={index}
                className={`v3-slot ${!slot.song ? 'empty' : ''} ${dragOverIndex === index ? 'drag-over' : ''} ${dragIndex === index ? 'dragging' : ''}`}
                draggable={!!slot.song}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <span className="v3-slot-drag" style={{ visibility: slot.song ? 'visible' : 'hidden' }}>⠿</span>
                <div className="v3-slot-song">
                  {slot.label && slot.song && (
                    <div className={`v3-slot-label ${slot.label}`}>{slot.label === 'sprint' ? 'Sprint' : slot.label.charAt(0).toUpperCase() + slot.label.slice(1)}</div>
                  )}
                  {slot.song ? (
                    <>
                      <span className="v3-slot-song-name">{slot.song.name}</span>
                      <span className="v3-slot-song-artist">{(slot.song.artists || []).join(', ')}</span>
                    </>
                  ) : (
                    <span className="v3-slot-empty-label">Drop a song here or click + from sidebar</span>
                  )}
                </div>
                <span className="v3-slot-bpm" style={!slot.song ? { color: 'var(--text-muted)' } : {}}>
                  {slot.song?.bpm || '—'}
                </span>
                <span className="v3-slot-plays">{slot.song ? `${slot.song.play_count || 0}×` : ''}</span>
                <span className="v3-slot-last">{slot.song ? formatLastPlayedShort(slot.song.last_played) : ''}</span>
                <div className="v3-slot-actions">
                  {slot.song && (
                    <>
                      <div className="v3-label-buttons">
                        {['opener', 'slow', 'sprint', 'closer'].map(l => (
                          <button
                            key={l}
                            className={`v3-label-btn ${slot.label === l ? l : ''}`}
                            onClick={() => toggleLabel(index, l)}
                            title={l}
                          >
                            {l[0].toUpperCase()}
                          </button>
                        ))}
                      </div>
                      <button
                        className="v3-newsoon-btn"
                        title="Add to New Soon playlist"
                        onClick={e => { e.stopPropagation(); addToNewSoon(slot.song.spotify_id); }}
                      >+NS</button>
                      <button
                        className="v3-play-btn"
                        title={currentlyPlaying === slot.song.spotify_id && isPlaying ? "Pause on Spotify" : "Play on Spotify"}
                        onClick={e => { e.stopPropagation(); playTrack(slot.song.spotify_id); }}
                      >
                        {currentlyPlaying === slot.song.spotify_id && isPlaying ? '⏸' : '▶'}
                      </button>
                      <button className="v3-remove-btn" onClick={() => removeSong(index)}>✕</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button 
            className={`v3-add-slot-btn ${addSlotDragOver ? 'drag-over' : ''}`}
            onClick={addEmptySlot}
            onDragOver={handleAddSlotDragOver}
            onDragLeave={handleAddSlotDragLeave}
            onDrop={handleAddSlotDrop}
          >
            + Add Slot
          </button>

          {/* Checklist - key forces re-render when slots change (including removals) */}
          {slots.length > 0 && (
            <div className="v3-checklist" key={`checklist-${slots.map(s => s.song?.id || 'empty').join('-')}`}>
              <h3>
                ✅ Playlist Checklist
                <span className="v3-checklist-count">{passedCount}/{verifiableItems.length}</span>
              </h3>
              <div className="v3-checklist-grid">
                <div className="v3-checklist-group-label hard">Hard Rules</div>
                {checklistItems.filter(c => c.group === 'hard').map((item, i) => (
                  <CheckItem key={i} {...item} />
                ))}

                <div className="v3-checklist-group-label structure">Structure</div>
                {checklistItems.filter(c => c.group === 'structure').map((item, i) => (
                  <CheckItem key={i} {...item} />
                ))}

                <div className="v3-checklist-group-label variety">Decade & Genre Variety</div>
                {checklistItems.filter(c => c.group === 'variety').map((item, i) => (
                  <CheckItem key={i} {...item} />
                ))}
              </div>
            </div>
          )}

          {/* BPM Flow */}
          {filledSlots.length > 0 && (
            <div className="v3-bpm-curve">
              <h3>BPM Flow</h3>
              <div className="v3-bpm-bars">
                {bpmData.map((d, i) => {
                  const maxBpm = 130;
                  const minBpm = 40;
                  const height = d.bpm 
                    ? Math.max(8, ((d.bpm - minBpm) / (maxBpm - minBpm)) * 100) 
                    : 10;
                  return (
                    <div
                      key={i}
                      className="v3-bpm-bar"
                      style={{
                        height: `${height}%`,
                        background: d.bpm ? bpmBarColor(d.bpm) : 'var(--text-muted)',
                        opacity: d.bpm ? 1 : 0.4
                      }}
                    >
                      <span className="v3-bpm-bar-value">{d.bpm || '?'}</span>
                      <span className="v3-bpm-bar-label">{d.position}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ===== RIGHT: SONG BROWSER ===== */}
        <div className="v3-sidebar">
          <div className="v3-sidebar-header">
            <div className="v3-sidebar-tabs">
              {['all', 'collections', 'top40', 'newsoon'].map(tab => (
                <button
                  key={tab}
                  className={`v3-sidebar-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'all' ? 'All Songs' : tab === 'collections' ? 'Spotify Library' : tab === 'top40' ? 'Top 40' : 'New Soon'}
                </button>
              ))}
            </div>
          </div>

          {/* Search bar (All Songs only) */}
          {activeTab === 'all' && (
            <div className="v3-search-bar">
              <input
                type="text"
                placeholder="Search songs, artists..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          )}

          {/* Shared filter bar — BPM chips + role/position buttons, persists across all tabs */}
          <div className="v3-shared-filter-bar">
            <BpmChips
              activeBpmRange={activeBpmRange}
              onSelect={handleBpmChipSelect}
              onClear={handleBpmChipClear}
              counts={bpmRangeCounts}
            />
            <RoleFilterBar
              roleFilter={roleFilter}
              onSelectRole={setRoleFilter}
              positionFilter={positionFilter}
              onSelectPosition={setPositionFilter}
            />
          </div>

          {/* All Songs Panel */}
          {activeTab === 'all' && (
            <div className="v3-sidebar-panel">
              <BPMNumberFilter
                bpmRangeMin={bpmRangeMin}
                bpmRangeMax={bpmRangeMax}
                setBpmRangeMin={(v) => { setBpmRangeMin(v); setActiveBpmRange(null); }}
                setBpmRangeMax={(v) => { setBpmRangeMax(v); setActiveBpmRange(null); }}
                minLengthMinutes={minLengthMinutes}
                minLengthSeconds={minLengthSeconds}
                setMinLengthMinutes={setMinLengthMinutes}
                setMinLengthSeconds={setMinLengthSeconds}
              />
              <div className="v3-sort-row">
                <button
                  className={`v3-sort-btn ${allSongsSort.field === 'name' ? 'active' : ''}`}
                  onClick={() => setAllSongsSort(prev => 
                    prev.field === 'name' 
                      ? { field: 'name', order: prev.order === 'asc' ? 'desc' : 'asc' }
                      : { field: 'name', order: 'asc' }
                  )}
                >
                  Name {allSongsSort.field === 'name' ? (allSongsSort.order === 'asc' ? '↑' : '↓') : ''}
                </button>
                <button
                  className={`v3-sort-btn ${allSongsSort.field === 'bpm' ? 'active' : ''}`}
                  onClick={() => setAllSongsSort(prev => 
                    prev.field === 'bpm' 
                      ? { field: 'bpm', order: prev.order === 'asc' ? 'desc' : 'asc' }
                      : { field: 'bpm', order: 'asc' }
                  )}
                >
                  BPM {allSongsSort.field === 'bpm' ? (allSongsSort.order === 'asc' ? '↑' : '↓') : ''}
                </button>
                <button
                  className={`v3-sort-btn ${allSongsSort.field === 'plays' ? 'active' : ''}`}
                  onClick={() => setAllSongsSort(prev => 
                    prev.field === 'plays' 
                      ? { field: 'plays', order: prev.order === 'asc' ? 'desc' : 'asc' }
                      : { field: 'plays', order: 'desc' }
                  )}
                >
                  Plays {allSongsSort.field === 'plays' ? (allSongsSort.order === 'asc' ? '↑' : '↓') : ''}
                </button>
                <button
                  className={`v3-sort-btn ${allSongsSort.field === 'last_played' ? 'active' : ''}`}
                  onClick={() => setAllSongsSort(prev => 
                    prev.field === 'last_played' 
                      ? { field: 'last_played', order: prev.order === 'asc' ? 'desc' : 'asc' }
                      : { field: 'last_played', order: 'desc' }
                  )}
                >
                  Last Played {allSongsSort.field === 'last_played' ? (allSongsSort.order === 'asc' ? '↑' : '↓') : ''}
                </button>
                <label className="v3-freshness-check" style={{ marginLeft: 'auto' }}>
                  <input
                    type="checkbox"
                    checked={!hideFresh}
                    onChange={(e) => setHideFresh(!e.target.checked)}
                  />
                  <span className="v3-check-label">Recent</span>
                </label>
              </div>
              <div className="v3-selected-bpm-label">
                All Songs · {sidebarSongs.filter(matchesRoleFilter).length} shown{sidebarTotal > sidebarSongs.length ? ` of ${sidebarTotal}` : ''}
              </div>
              <SongList
                songs={sidebarSongs.filter(matchesRoleFilter)}
                loading={sidebarLoading}
                onAdd={handleAddSong}
                onDragStart={handleSidebarDragStart}
                onPlay={playTrack}
                showBpm
                currentlyPlaying={currentlyPlaying}
                isPlaying={isPlaying}
                hideFresh={hideFresh}
                freshMonths={settings.freshMonths}
              />
            </div>
          )}

          {/* Top 40 Panel */}
          {activeTab === 'collections' && (
            <div className="v3-sidebar-panel">
              {!activeCollection ? (
                <div className="v3-collections-grid">
                  {collections.map(c => (
                    <button
                      key={c.id}
                      className="v3-collection-folder"
                      onClick={() => fetchCollectionSongs(c.id)}
                    >
                      <span className="v3-collection-icon">📁</span>
                      <span className="v3-collection-name">{c.name}</span>
                      <span className="v3-collection-count">{c.song_count}</span>
                    </button>
                  ))}
                  {collections.length === 0 && !collectionsLoaded && (
                    <div className="v3-loading">Loading collections...</div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <button
                    className="v3-btn v3-btn-sm"
                    style={{ margin: '8px 16px', flexShrink: 0 }}
                    onClick={() => { setActiveCollection(null); setCollectionSongs([]); }}
                  >
                    ← Back
                  </button>
                  <div className="v3-selected-bpm-label" style={{ padding: '0 16px 4px', flexShrink: 0 }}>
                    📁 {collections.find(c => c.id === activeCollection)?.name} · {collectionSongs.filter(t => matchesBpmFilter(t.bpm) && matchesRoleFilter(t)).length} songs
                  </div>
                  <div className="v3-sort-row" style={{ padding: '0 16px 8px', flexShrink: 0 }}>
                    {[
                      { key: 'name', label: 'Name' },
                      { key: 'bpm', label: 'BPM' },
                      { key: 'plays', label: 'Plays' },
                      { key: 'last_played', label: 'Last Played' },
                    ].map(s => (
                      <button
                        key={s.key}
                        className={`v3-sort-btn ${collectionSort === s.key ? 'active' : ''}`}
                        onClick={() => setCollectionSort(s.key)}
                      >{s.label}</button>
                    ))}
                    <label className="v3-freshness-check" style={{ marginLeft: 'auto' }}>
                      <input
                        type="checkbox"
                        checked={!hideFresh}
                        onChange={(e) => setHideFresh(!e.target.checked)}
                      />
                      <span className="v3-check-label">Recent</span>
                    </label>
                  </div>
                  {collectionLoading ? (
                    <div className="v3-loading">Loading songs...</div>
                  ) : (
                    <SongList
                      songs={collectionSongs.filter(t => matchesBpmFilter(t.bpm) && matchesRoleFilter(t))}
                      loading={false}
                      onAdd={handleAddSong}
                      onDragStart={handleSidebarDragStart}
                      onPlay={playTrack}
                      showBpm
                      currentlyPlaying={currentlyPlaying}
                      isPlaying={isPlaying}
                      hideFresh={hideFresh}
                      freshMonths={settings.freshMonths}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'top40' && (
            <div className="v3-sidebar-panel">
              <div className="v3-selected-bpm-label">🔥 Billboard Top 40</div>
              <div className="v3-sort-row">
                {[
                  { key: 'rank', label: 'Rank' },
                  { key: 'bpm', label: 'BPM' },
                  { key: 'plays', label: 'Plays' },
                ].map(s => (
                  <button
                    key={s.key}
                    className={`v3-sort-btn ${top40Sort === s.key ? 'active' : ''}`}
                    onClick={() => setTop40Sort(s.key)}
                  >{s.label}</button>
                ))}
                <label className="v3-freshness-check" style={{ marginLeft: 'auto' }}>
                  <input
                    type="checkbox"
                    checked={!hideFresh}
                    onChange={(e) => setHideFresh(!e.target.checked)}
                  />
                  <span className="v3-check-label">Recent</span>
                </label>
              </div>
              {top40Loading ? (
                <div className="v3-loading">Loading Top 40...</div>
              ) : spotifyError && top40Tracks.length === 0 ? (
                <div className="v3-spotify-error">
                  <div className="v3-error-icon">⚠️</div>
                  <div className="v3-error-message">{spotifyError}</div>
                  {spotifyAuthNeeded && (
                    <a href={`${API_BASE.replace('/api', '')}/auth/spotify`} className="v3-btn v3-btn-primary v3-auth-btn">
                      🔑 Connect Spotify
                    </a>
                  )}
                  <button className="v3-btn v3-btn-sm" onClick={fetchTop40} style={{ marginTop: '8px' }}>
                    🔄 Retry
                  </button>
                </div>
              ) : (
                <div className="v3-song-list">
                  {[...top40Tracks].filter(t => {
                    if (!matchesBpmFilter(t.bpm)) return false;
                    // Freshness filter
                    if (!hideFresh) return true;
                    if (!t.last_played) return true;
                    const months = (settings.freshMonths || 6);
                    const cutoff = new Date();
                    cutoff.setMonth(cutoff.getMonth() - months);
                    return new Date(t.last_played) < cutoff;
                  }).sort((a, b) => {
                    if (top40Sort === 'bpm') return (a.bpm || 999) - (b.bpm || 999);
                    if (top40Sort === 'plays') return (b.play_count || 0) - (a.play_count || 0);
                    return (a.rank || 0) - (b.rank || 0);
                  }).map((track, i) => {
                    const isRecentlyPlayed = !hideFresh && track.last_played && (() => {
                      const cutoff = new Date();
                      cutoff.setMonth(cutoff.getMonth() - (settings.freshMonths || 6));
                      return new Date(track.last_played) >= cutoff;
                    })();
                    return (
                    <div
                      key={i}
                      className={`v3-song-item ${isRecentlyPlayed ? 'hidden-recent' : ''}`}
                      draggable
                      onDragStart={() => handleSidebarDragStart(track)}
                    >
                      <div className="v3-song-item-info">
                        <div className="v3-song-item-name">
                          <span className="v3-rank-badge">#{track.rank}</span> {track.name}
                        </div>
                        <div className="v3-song-item-meta">
                          <span className="v3-meta-artist">{track.artists && track.artist_ids ? track.artists.map((artist, i) => <span key={i}>{i > 0 && ', '}<a href={`/artist/${track.artist_ids[i]}`} target="_blank" rel="noreferrer">{artist}</a></span>) : track.artists.join(', ')}{track.in_library && <span className="v3-in-library"> · In lib</span>}</span>
                          {track.last_played && (
                            <span className="v3-meta-last">Last: {track.last_played_playlist_id
                              ? <a href={`https://open.spotify.com/playlist/${track.last_played_playlist_id}`} target="_blank" rel="noreferrer">{formatLastPlayedShort(track.last_played)}</a>
                              : formatLastPlayedShort(track.last_played)
                            }</span>
                          )}
                          {track.added_at && <span className="v3-meta-added">Added: {formatLastPlayed(track.added_at)}</span>}
                        </div>
                      </div>
                      <span className="v3-song-item-plays">{track.play_count ? `${track.play_count}×` : ''}</span>
                      <span className="v3-song-item-bpm">{track.bpm || '—'}</span>
                      <button className="v3-play-btn-sm" title="Play on Spotify" onClick={e => { e.stopPropagation(); playTrack(track.spotify_id); }}>▶</button>
                      <button className="v3-song-item-add" onClick={() => handleAddSong(track)}>+</button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* New Soon Panel */}
          {activeTab === 'newsoon' && (
            <div className="v3-sidebar-panel">
              <div className="v3-sort-row">
                <button
                  className={`v3-sort-btn ${newSoonSort === 'date' ? 'active' : ''}`}
                  onClick={() => setNewSoonSort('date')}
                >
                  Date Added ↓
                </button>
                <button
                  className={`v3-sort-btn ${newSoonSort === 'bpm' ? 'active' : ''}`}
                  onClick={() => setNewSoonSort('bpm')}
                >
                  BPM ↓
                </button>
                <label className="v3-freshness-check" style={{ marginLeft: 'auto' }}>
                  <input
                    type="checkbox"
                    checked={!hideFresh}
                    onChange={(e) => setHideFresh(!e.target.checked)}
                  />
                  <span className="v3-check-label">Recent</span>
                </label>
              </div>
              <div className="v3-selected-bpm-label">
                📋 New Soon Playlist · {sortedNewSoon.length}
                {sortedNewSoon.length !== newSoonTracks.length && ` of ${newSoonTracks.length}`} songs
              </div>
              {newSoonLoading ? (
                <div className="v3-loading">Loading New Soon...</div>
              ) : (
                <div className="v3-song-list">
                  {sortedNewSoon.map((track, i) => {
                    const isRecentlyPlayed = !hideFresh && track.last_played && (() => {
                      const cutoff = new Date();
                      cutoff.setMonth(cutoff.getMonth() - (settings.freshMonths || 6));
                      return new Date(track.last_played) >= cutoff;
                    })();
                    return (
                    <div
                      key={i}
                      className={`v3-song-item ${isRecentlyPlayed ? 'hidden-recent' : ''}`}
                      draggable
                      onDragStart={() => handleSidebarDragStart(track)}
                    >
                      <div className="v3-song-item-info">
                        <div className="v3-song-item-name">{track.id ? <a href={`/song/${track.id}`} target="_blank" rel="noreferrer" style={{ color: 'white' }}>{track.name}</a> : track.name}</div>
                        <div className="v3-song-item-meta">
                          <span className="v3-meta-artist">{track.artists && track.artist_ids ? track.artists.map((artist, i) => <span key={i}>{i > 0 && ', '}<a href={`/artist/${track.artist_ids[i]}`} target="_blank" rel="noreferrer">{artist}</a></span>) : track.artists.join(', ')}</span>
                          {track.last_played && (
                            <span className="v3-meta-last">Last: {track.last_played_playlist_id
                              ? <a href={`https://open.spotify.com/playlist/${track.last_played_playlist_id}`} target="_blank" rel="noreferrer">{formatLastPlayedShort(track.last_played)}</a>
                              : formatLastPlayedShort(track.last_played)
                            }</span>
                          )}
                          {track.added_at && <span className="v3-meta-added">Added: {formatLastPlayed(track.added_at)}</span>}
                        </div>
                      </div>
                      <span className="v3-song-item-plays">{track.play_count ? `${track.play_count}×` : ''}</span>
                      <span className="v3-song-item-bpm">{track.bpm || '—'}</span>
                      <button className="v3-play-btn-sm" title="Play on Spotify" onClick={e => { e.stopPropagation(); playTrack(track.spotify_id); }}>▶</button>
                      <button className="v3-song-item-add" onClick={() => handleAddSong(track)}>+</button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="v3-status-bar">
            <span>{filledSlots.length}/{slots.length} slots filled</span>
            <span>{slots.length - filledSlots.length} empty · {noDuplicateArtists ? '0' : '⚠️'} artist dupes</span>
          </div>
        </div>
      </div>

      {/* ===== MODALS ===== */}
      {showSettings && (
        <Modal title="⚙️ Settings" onClose={() => setShowSettings(false)}>
          <div className="v3-settings-form">
            <div className="v3-setting-row">
              <label>New Soon Playlist ID</label>
              <div style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center' }}>
                <input
                  style={{ flex: 1 }}
                  value={settings.newSoonPlaylistId}
                  onChange={e => setSettings({ ...settings, newSoonPlaylistId: e.target.value })}
                />
                <button
                  className="v3-btn v3-btn-sm"
                  onClick={previewSyncNewSoon}
                  disabled={syncingNewSoon || !settings.newSoonPlaylistId || !!syncNewSoonPreview}
                  title={`Preview: tracks played in the last ${settings.freshMonths || 6} months will be removed`}
                >
                  {syncingNewSoon && !syncNewSoonPreview ? '⏳ Loading…' : '🧹 Clean up'}
                </button>
              </div>
            </div>
            {syncNewSoonPreview && (
              <div
                className="v3-setting-row"
                style={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 8,
                  padding: 10,
                  border: '1px solid #facc15',
                  borderRadius: 6,
                  background: 'rgba(250, 204, 21, 0.06)',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#facc15' }}>
                  Preview: {syncNewSoonPreview.would_remove} track{syncNewSoonPreview.would_remove === 1 ? '' : 's'} would be removed
                  {' · '}
                  {syncNewSoonPreview.kept} kept · {syncNewSoonPreview.freshMonths}mo window
                </div>
                {syncNewSoonPreview.would_remove > 0 && (
                  <div
                    style={{
                      maxHeight: 220,
                      overflowY: 'auto',
                      fontSize: 12,
                      background: 'rgba(0,0,0,0.25)',
                      borderRadius: 4,
                      padding: 8,
                      lineHeight: 1.5,
                    }}
                  >
                    {syncNewSoonPreview.removed_tracks.map((t, i) => (
                      <div key={t.spotify_id || i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                        <span style={{ color: '#9ca3af', flexShrink: 0 }}>
                          {t.last_played ? new Date(t.last_played).toISOString().slice(0, 10) : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    className="v3-btn v3-btn-sm"
                    onClick={cancelSyncNewSoon}
                    disabled={syncingNewSoon}
                  >
                    Cancel
                  </button>
                  <button
                    className="v3-btn v3-btn-sm v3-btn-primary"
                    onClick={confirmSyncNewSoon}
                    disabled={syncingNewSoon || syncNewSoonPreview.would_remove === 0}
                  >
                    {syncingNewSoon ? '⏳ Removing…' : `✅ Confirm — remove ${syncNewSoonPreview.would_remove}`}
                  </button>
                </div>
              </div>
            )}
            {syncNewSoonResult && (
              <div
                className="v3-setting-row"
                style={{
                  fontSize: 12,
                  color: syncNewSoonResult.ok ? '#4ade80' : '#f87171',
                  paddingLeft: 8,
                }}
              >
                {syncNewSoonResult.ok
                  ? syncNewSoonResult.nothingToDo
                    ? `✓ Nothing to remove — none of the ${syncNewSoonResult.kept} tracks were played in the freshness window.`
                    : `✅ Removed ${syncNewSoonResult.removed} recently-played track${syncNewSoonResult.removed === 1 ? '' : 's'}, kept ${syncNewSoonResult.kept}.`
                  : `❌ ${syncNewSoonResult.error}`}
              </div>
            )}
            <div className="v3-setting-row">
              <label>Top 40 Playlist ID</label>
              <input
                value={settings.top40PlaylistId}
                onChange={e => setSettings({ ...settings, top40PlaylistId: e.target.value })}
              />
            </div>
            <div className="v3-setting-row">
              <label>Freshness Threshold (months)</label>
              <input
                type="number"
                value={settings.freshMonths}
                onChange={e => setSettings({ ...settings, freshMonths: parseInt(e.target.value) })}
              />
            </div>
            <div className="v3-setting-row">
              <label>Default Target Duration</label>
              <select
                value={settings.defaultTargetMinutes}
                onChange={e => setSettings({ ...settings, defaultTargetMinutes: parseInt(e.target.value) })}
              >
                <option value={49}>49 min (13 songs)</option>
                <option value={50}>50 min (13 songs)</option>
                <option value={51}>51 min (14 songs)</option>
                <option value={52}>52 min (14 songs)</option>
              </select>
            </div>
            <button className="v3-btn v3-btn-primary" onClick={() => { saveSettings(settings); setShowSettings(false); }}>
              Save Settings
            </button>
          </div>
        </Modal>
      )}

      {showOpenPlaylist && (
        <Modal title="📂 Open" onClose={() => setShowOpenPlaylist(false)}>
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {drafts.length > 0 && (
              <>
                <div className="v3-open-section-label">Drafts</div>
                <DraftsList
                  drafts={drafts}
                  onLoad={(id) => { loadDraft(id); setShowOpenPlaylist(false); }}
                  onDelete={deleteDraft}
                />
              </>
            )}
            <div className="v3-open-section-label" style={{ marginTop: drafts.length > 0 ? '16px' : '0' }}>Published Playlists</div>
            <div className="v3-playlists-list">
              {playlistsList.map(p => (
                <div key={p.id} className="v3-playlist-list-item" onClick={() => loadPlaylist(p.id)}>
                  <span className="v3-playlist-list-name">{p.name}</span>
                  <span className="v3-playlist-list-meta">
                    {p.track_count} songs · {p.playlist_date ? new Date(p.playlist_date).toLocaleDateString() : 'No date'}
                  </span>
                  <div className="v3-playlist-list-actions">
                    {p.spotify_id ? (
                      <span
                        className="v3-playlist-visibility-icon"
                        title={p.is_public ? 'Public on Spotify' : 'Private on Spotify'}
                      >
                        {p.is_public ? '👁️' : '🔒'}
                      </span>
                    ) : (
                      <span className="v3-playlist-no-spotify" title="Not on Spotify yet">—</span>
                    )}
                    <button
                      className="v3-playlist-delete-btn"
                      onClick={(e) => { e.stopPropagation(); deletePlaylist(p.id, p.name); }}
                      title="Delete playlist"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// =====================================================
// SUB-COMPONENTS
// =====================================================

function CheckItem({ label, passed, unknown }) {
  const cls = unknown ? 'unknown' : passed ? 'passed' : 'failed';
  return (
    <label className="v3-check-item">
      <span className={`v3-check-circle ${cls}`}>
        {unknown ? '—' : passed ? '✓' : '✕'}
      </span>
      <span style={unknown ? { color: 'var(--v3-text-dim, #555)' } : passed ? {} : { color: 'var(--v3-text-dim, #888)' }}>{label}</span>
    </label>
  );
}

function SongList({ songs, loading, onAdd, onDragStart, showBpm, onPlay, currentlyPlaying, isPlaying, hideFresh, freshMonths }) {
  if (loading) return <div className="v3-loading">Loading songs...</div>;
  
  return (
    <div className="v3-song-list">
      {songs.map((song, i) => {
        const isThisPlaying = currentlyPlaying === song.spotify_id && isPlaying;
        // Determine if this song would be hidden by freshness filter
        const isRecentlyPlayed = !hideFresh && song.last_played && (() => {
          const cutoff = new Date();
          cutoff.setMonth(cutoff.getMonth() - (freshMonths || 6));
          return new Date(song.last_played) >= cutoff;
        })();
        
        return (
          <div
            key={song.id || i}
            className={`v3-song-item ${isRecentlyPlayed ? 'hidden-recent' : ''}`}
            draggable
            onDragStart={() => onDragStart(song)}
          >
            <div className="v3-song-item-info">
              <div className="v3-song-item-name">{song.id ? <a href={`/song/${song.id}`} target="_blank" rel="noreferrer" style={{ color: 'white' }}>{song.name}</a> : song.name}</div>
              <div className="v3-song-item-meta">
                <span className="v3-meta-artist">{song.artists && song.artist_ids ? song.artists.map((artist, i) => <span key={i}>{i > 0 && ', '}<a href={`/artist/${song.artist_ids[i]}`} target="_blank" rel="noreferrer">{artist}</a></span>) : (song.artists || []).join(', ')}</span>
                {song.last_played && (
                  <span className="v3-meta-last">Last: {song.last_played_playlist_id
                    ? <a href={`https://open.spotify.com/playlist/${song.last_played_playlist_id}`} target="_blank" rel="noreferrer">{formatLastPlayedShort(song.last_played)}</a>
                    : formatLastPlayedShort(song.last_played)
                  }</span>
                )}
              </div>
            </div>
            <span className="v3-song-item-plays">{song.play_count ? `${song.play_count}×` : ''}</span>
            {showBpm && <span className="v3-song-item-bpm">{song.bpm || '—'}</span>}
            <button
              className="v3-play-btn-sm"
              title={isThisPlaying ? "Pause on Spotify" : "Play on Spotify"}
              onClick={e => { e.stopPropagation(); onPlay && onPlay(song.spotify_id); }}
            >
              {isThisPlaying ? '⏸' : '▶'}
            </button>
            <button className="v3-song-item-add" onClick={() => onAdd(song)}>+</button>
          </div>
        );
      })}
      {songs.length === 0 && !loading && (
        <div className="v3-empty-message">No songs found</div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="v3-modal-overlay" onClick={onClose}>
      <div className="v3-modal" onClick={e => e.stopPropagation()}>
        <div className="v3-modal-header">
          <h3>{title}</h3>
          <button className="v3-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="v3-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

function DraftsList({ drafts, onLoad, onDelete }) {
  const [expanded, setExpanded] = useState(null);

  const grouped = {};
  drafts.forEach(d => {
    if (!grouped[d.name]) grouped[d.name] = [];
    grouped[d.name].push(d);
  });
  const groupNames = Object.keys(grouped).sort((a, b) => {
    const aMax = Math.max(...grouped[a].map(d => new Date(d.updated_at || d.created_at).getTime()));
    const bMax = Math.max(...grouped[b].map(d => new Date(d.updated_at || d.created_at).getTime()));
    return bMax - aMax;
  });

  return (
    <div className="v3-drafts-list">
      {groupNames.map(name => {
        const versions = grouped[name];
        const latest = versions[0];
        const isExpanded = expanded === name;
        const hasVersions = versions.length > 1;

        return (
          <div key={name} className="v3-draft-group">
            <div className="v3-draft-item v3-draft-item-main" onClick={() => onLoad(latest.id)}>
              <div className="v3-draft-left">
                {!latest.spotify_playlist_id && <span className="v3-draft-no-spotify" title="Not yet on Spotify">⚠️</span>}
                <span className="v3-draft-name">{name}</span>
                {hasVersions && (
                  <button
                    className="v3-draft-expand"
                    onClick={(e) => { e.stopPropagation(); setExpanded(isExpanded ? null : name); }}
                    title={`${versions.length} versions`}
                  >
                    {isExpanded ? '▾' : '▸'} {versions.length}v
                  </button>
                )}
              </div>
              <div className="v3-draft-right">
                <span className="v3-draft-meta">
                  {latest.slot_count} slots · {new Date(latest.updated_at || latest.created_at).toLocaleDateString()}
                </span>
                <button className="v3-draft-delete" onClick={(e) => onDelete(latest.id, e)} title="Delete">🗑</button>
              </div>
            </div>
            {isExpanded && versions.length > 1 && (
              <div className="v3-draft-versions">
                {versions.slice(1).map((v, i) => (
                  <div key={v.id} className="v3-draft-item v3-draft-item-version" onClick={() => onLoad(v.id)}>
                    <div className="v3-draft-left">
                      <span className="v3-draft-version-label">v{versions.length - i - 1}</span>
                      <span className="v3-draft-meta">{v.slot_count} slots</span>
                    </div>
                    <div className="v3-draft-right">
                      <span className="v3-draft-meta">
                        {new Date(v.updated_at || v.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                      <button className="v3-draft-delete" onClick={(e) => onDelete(v.id, e)} title="Delete version">🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default GeneratorV3;
